import { useEffect, useState } from "react";
import { Download, ExternalLink, QrCode } from "lucide-react";

interface Sucursal { id: number; nombre: string; ciudad?: string; }
interface RecursoQr { id: number; nombre: string; capacidad: number; }
interface ZonaQr { id: number; nombre: string; permiteSeleccionMesa?: boolean; recursos?: RecursoQr[]; }

const QR_SIZE = 280;

function qrUrl(texto: string, size = QR_SIZE) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(texto)}&format=png&margin=10`;
}

function descargarQR(url: string, nombre: string) {
  const a = document.createElement("a");
  a.href = url + "&download=1";
  a.download = nombre + ".png";
  a.click();
}

function QrCard({ titulo, descripcion, url, icono = "📋", colorFondo = "#f8f8f8" }: {
  titulo: string; descripcion: string; url: string; icono?: string; colorFondo?: string;
}) {
  const imgUrl = qrUrl(url);
  return (
    <div className="card p-5 space-y-4">
      <div>
        <h3 className="font-semibold text-ink-800 flex items-center gap-2">
          <span className="text-xl">{icono}</span> {titulo}
        </h3>
        <p className="text-xs text-ink-500 mt-0.5">{descripcion}</p>
      </div>
      <div className="flex flex-col items-center gap-3 p-4 rounded-xl" style={{ background: colorFondo }}>
        <img src={imgUrl} alt={`QR ${titulo}`} className="w-52 h-52 rounded-lg shadow-sm" />
        <p className="text-xs text-ink-400 text-center font-mono break-all max-w-[200px]">{url}</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => descargarQR(imgUrl, titulo.replace(/\s+/g, '-').toLowerCase())}
          className="btn-primary flex items-center gap-2 flex-1 justify-center text-sm"
        >
          <Download className="size-4" /> Descargar PNG
        </button>
        <a href={url} target="_blank" rel="noreferrer"
          className="btn-secondary flex items-center gap-2 text-sm px-3">
          <ExternalLink className="size-4" />
        </a>
      </div>
      <p className="text-xs text-ink-400 bg-ink-50 rounded-lg p-2">
        💡 Imprime este QR y colócalo en las mesas. El cliente escanea con la cámara del celular.
      </p>
    </div>
  );
}

export function QrCodesPage() {
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [sucId, setSucId] = useState<number | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [zonas, setZonas] = useState<ZonaQr[]>([]);
  const [zonaSelId, setZonaSelId] = useState<number | null>(null);

  useEffect(() => {
    setBaseUrl(window.location.origin);
    fetch("/api/v1/sucursales")
      .then(r => r.json())
      .then((d: any[]) => {
        if (!Array.isArray(d)) return;
        const list = d.map(s => ({ id: s.id, nombre: s.nombre, ciudad: s.ciudad }));
        setSucursales(list);
        if (list.length > 0) setSucId(list[0].id);
      })
      .catch(() => {});
  }, []);

  // Cargar zonas con recursos cuando cambia la sucursal
  useEffect(() => {
    if (!sucId) return;
    setZonas([]);
    setZonaSelId(null);
    fetch("/api/v1/reservas/reservar-info")
      .then(r => r.json())
      .then((d: any) => {
        const conMesas: ZonaQr[] = (d.zonas ?? []).filter(
          (z: ZonaQr) => z.permiteSeleccionMesa && (z.recursos ?? []).length > 0
        );
        setZonas(conMesas);
        if (conMesas.length === 1) setZonaSelId(conMesas[0].id);
      })
      .catch(() => {});
  }, [sucId]);

  const reservarUrl = sucId ? `${baseUrl}/reservar?sucursal=${sucId}` : "";
  const listaEsperaUrl = sucId ? `${baseUrl}/lista-espera?sucursal=${sucId}` : "";
  const menuUrl = sucId ? `${baseUrl}/menu/sucursal/${sucId}` : "";
  const zonaSelObj = zonas.find(z => z.id === zonaSelId);
  const recursos = zonaSelObj?.recursos ?? [];
  const labelRecurso = zonaSelObj?.nombre.toLowerCase().includes('bowl') || zonaSelObj?.nombre.toLowerCase().includes('pista') ? 'Pista' : 'Mesa';

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink-800 flex items-center gap-2">
          <QrCode className="size-6 text-brand-600" /> Códigos QR
        </h1>
        <p className="text-ink-500 text-sm mt-1">
          Genera y descarga los QR para imprimir en mesas, menús y puntos de acceso del local.
        </p>
      </div>

      {/* Selector de sucursal */}
      {sucursales.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {sucursales.map(s => (
            <button key={s.id} onClick={() => setSucId(s.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sucId === s.id ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
              {s.nombre}
            </button>
          ))}
        </div>
      )}

      {sucId && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <QrCard
              titulo="Menú Digital"
              descripcion="El cliente escanea y ve el menú completo. Puede llamar al mesonero o pedir la cuenta."
              url={menuUrl}
              icono="🍽️"
              colorFondo="#fdf6ff"
            />
            <QrCard
              titulo="Hacer una Reserva"
              descripcion="El cliente escanea y reserva su mesa o pista directamente desde el celular."
              url={reservarUrl}
              icono="📅"
              colorFondo="#f0f9ff"
            />
            <QrCard
              titulo="Lista de espera (walk-in)"
              descripcion="Para colocar en la puerta: el cliente sin reserva escanea, se anota y le avisamos por WhatsApp cuando tenga mesa."
              url={listaEsperaUrl}
              icono="⏳"
              colorFondo="#fff7ed"
            />
          </div>

          {/* QR por zona / mesa / pista */}
          <div className="card p-5 space-y-4">
            <div>
              <h3 className="font-semibold text-ink-800">QR por mesa o pista</h3>
              <p className="text-xs text-ink-500 mt-0.5">
                Cuando el cliente escanea este QR, el sistema detecta automáticamente en qué
                {" "}{labelRecurso.toLowerCase()} está — sin que tenga que escribirlo.
              </p>
            </div>

            {/* Selector de zona (si hay varias) */}
            {zonas.length === 0 ? (
              <p className="text-sm text-ink-400 py-2">
                No hay zonas con selección de mesa habilitada.
                Activá "Permite elegir mesa" en <strong>Mi Sucursal → Zonas</strong>.
              </p>
            ) : (
              <>
                {zonas.length > 1 && (
                  <div>
                    <p className="text-xs font-medium text-ink-500 mb-2">Seleccioná la zona:</p>
                    <div className="flex flex-wrap gap-2">
                      {zonas.map(z => (
                        <button key={z.id} onClick={() => setZonaSelId(z.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${zonaSelId === z.id ? "bg-brand-600 text-white border-brand-600" : "bg-white text-ink-700 border-ink-200 hover:border-brand-400"}`}>
                          {z.nombre}
                          <span className="ml-1.5 text-xs opacity-70">({(z.recursos ?? []).length})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {zonaSelObj && recursos.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-ink-600">
                        {zonaSelObj.nombre} — {recursos.length} {labelRecurso.toLowerCase()}s
                      </p>
                      <button
                        onClick={() => {
                          recursos.forEach(r => {
                            const url = `${baseUrl}/menu/sucursal/${sucId}/mesa/${r.id}`;
                            setTimeout(() => descargarQR(qrUrl(url, 300), `${labelRecurso.toLowerCase()}-${r.nombre.replace(/\s+/g,'-')}`), 200 * recursos.indexOf(r));
                          });
                        }}
                        className="text-xs text-brand-600 hover:underline flex items-center gap-1"
                      >
                        <Download className="size-3" /> Descargar todos
                      </button>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {recursos.map(r => {
                        const urlMesa = `${baseUrl}/menu/sucursal/${sucId}/mesa/${r.id}`;
                        const imgMesa = qrUrl(urlMesa, 150);
                        return (
                          <div key={r.id} className="flex flex-col items-center gap-2 p-3 bg-ink-50 rounded-xl border border-ink-100">
                            <p className="text-xs font-bold text-ink-700">{r.nombre}</p>
                            {r.capacidad > 0 && (
                              <p className="text-[10px] text-ink-400">{r.capacidad} personas</p>
                            )}
                            <img src={imgMesa} alt={r.nombre} className="w-28 h-28 rounded shadow-sm" />
                            <div className="flex gap-2">
                              <button
                                onClick={() => descargarQR(qrUrl(urlMesa, 400), `${labelRecurso.toLowerCase()}-${r.nombre.replace(/\s+/g,'-')}`)}
                                className="text-xs text-brand-600 hover:underline flex items-center gap-1"
                              >
                                <Download className="size-3" /> Descargar
                              </button>
                              <a href={urlMesa} target="_blank" rel="noreferrer"
                                className="text-xs text-ink-400 hover:text-ink-600">
                                <ExternalLink className="size-3" />
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Links cruzados */}
          <div className="card p-5 bg-brand-50 border-brand-200">
            <h3 className="font-semibold text-brand-800 mb-2">📌 Conexión menú ↔ reservas</h3>
            <p className="text-xs text-brand-700 mb-3">
              El menú digital incluye el botón <strong>"Reservar mesa"</strong> y la página de
              reservas incluye el link al menú.
            </p>
            <div className="space-y-1 text-xs font-mono text-brand-600">
              <p>Menú → {menuUrl}</p>
              <p>Reservas → {reservarUrl}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
